import { Badge } from '@/components/UI/Badge';
import { cn } from '@/util/cn';
import { ArrowRight, Eye, EyeOff } from 'lucide-react';
import { FC, useState } from 'react';

import { DiffField } from '../../types/erpSync.types';
import { formatDiffValue, maskDiffValue } from '../../utils/diffValueDisplay';

const chipBase = 'rounded-lg border px-3 py-2 text-sm break-words';
const prevChip = cn(chipBase, 'flex-1 bg-muted/40 text-muted-foreground');
const newChipChanged = cn(
  chipBase,
  'flex-1 border-primary/40 bg-primary/5 font-semibold'
);
const newChipNeutral = cn(chipBase, 'flex-1 bg-muted/40 text-muted-foreground');

const MiniLabel: FC<{ children: string }> = ({ children }) => (
  <div className="text-muted-foreground mb-1.5 text-[10px] font-semibold tracking-wide uppercase">
    {children}
  </div>
);

/**
 * Renders one `DiffSummary.fields` entry (PBI #6095 ERP Sync tab) exactly as the prototype's
 * per-field diff row: a 4-column grid `[label] [Previous] [→] [New]` on one line (stacking on
 * mobile), rows separated by a bottom divider. The "Previous" chip is muted (struck through when
 * the value is being replaced/removed) and the "New" chip is highlighted for a real change. Fields
 * flagged `sensitive` are masked by default with a reveal/hide toggle (deliberate re-open of R7 —
 * see {@link maskDiffValue}). A first-ever change (`isInitialCapture`) shows a "First submission"
 * placeholder for the previous value and an "Initial capture" badge on the new value.
 */
export const DiffRow: FC<{ field: DiffField; isInitialCapture: boolean }> = ({
  field,
  isInitialCapture
}) => {
  const [revealed, setRevealed] = useState(false);
  const masked = field.sensitive && !revealed;
  const show = (value: string | null) =>
    masked ? maskDiffValue(value) : formatDiffValue(value);

  const revealToggle = field.sensitive ? (
    <button
      type="button"
      onClick={() => setRevealed((prev) => !prev)}
      className="text-muted-foreground hover:text-foreground ml-1 shrink-0"
      aria-label={revealed ? 'Hide value' : 'Reveal value'}
      title={revealed ? 'Hide value' : 'Reveal value'}
    >
      {revealed ? (
        <EyeOff className="h-3.5 w-3.5" aria-hidden />
      ) : (
        <Eye className="h-3.5 w-3.5" aria-hidden />
      )}
    </button>
  ) : null;

  return (
    <div className="grid grid-cols-1 gap-x-4 gap-y-2 border-b py-4 last:border-b-0 sm:grid-cols-[190px_1fr_28px_1fr] sm:items-stretch">
      <div className="flex items-center sm:self-start sm:pt-1.5">
        <span className="text-foreground text-sm font-medium">{field.label}</span>
        {revealToggle}
      </div>

      <div className="flex flex-col sm:h-full">
        <MiniLabel>Previous</MiniLabel>
        {isInitialCapture ? (
          <div className={cn(prevChip, 'italic')}>First submission</div>
        ) : (
          <div
            className={cn(
              prevChip,
              field.changeType === 'modified' && 'line-through',
              field.changeType === 'removed' && 'text-destructive line-through'
            )}
          >
            {show(field.oldValue)}
          </div>
        )}
      </div>

      <ArrowRight
        className="text-muted-foreground hidden h-4 w-4 sm:mt-[26px] sm:block sm:self-start sm:justify-self-center"
        aria-hidden
      />

      <div className="flex flex-col sm:h-full">
        <MiniLabel>New</MiniLabel>
        <div className="flex flex-1 flex-wrap items-stretch gap-2">
          <div
            className={
              field.changeType === 'removed' || field.changeType === 'unchanged'
                ? newChipNeutral
                : newChipChanged
            }
          >
            {show(field.newValue)}
          </div>
          {isInitialCapture && (
            <Badge variant="soft" color="info" size="sm" className="self-center">
              Initial capture
            </Badge>
          )}
        </div>
      </div>
    </div>
  );
};
